/**
 * AI Storybook — who is who in the parent's photos: the rules of the photo
 * step, as pure functions (no React), so the builder, the book page's "Update
 * photos" dialog and the tests share one copy.
 *
 * The same rules as the server (docs/storybook-identity-lock.md §2.2): a face
 * belongs to one person of the cast; a person is in a photo at most once and
 * in at most limits.maxRefsPerPerson photos; a face whose box is under
 * limits.minFacePx photo pixels on its shorter side can't be used; a friend is
 * never photographed. Only photos with at least one face of the cast are sent.
 */

import type {
  StorybookFaceBox,
  StorybookFaceTag,
  StorybookLimits,
  StorybookPersonId,
  StorybookPhotoUpload,
} from '@/lib/storybook-api';
import { STORYBOOK_DEFAULT_MAX_PHOTO_BYTES, STORYBOOK_DEFAULT_PHOTO_LONG_EDGE } from '@/lib/storybook-photo';

/** A face the parent marked as someone who is not in the book. */
export const NOT_IN_BOOK = 'none';

export interface StorybookTaggedFace {
  key: string;
  box: StorybookFaceBox;
  /** Found by the in-browser detector, or placed by hand (movable, resizable). */
  source: 'auto' | 'manual';
  /** A person's key, NOT_IN_BOOK, or null while nobody was chosen yet. */
  person: string | null;
}

export interface StorybookPhotoItem {
  key: string;
  /** The prepared JPEG (storybook-photo.ts), exactly as it would be sent. */
  dataUrl: string;
  width: number;
  height: number;
  /** Decoded bytes. */
  bytes: number;
  /** pending: the detector is still looking; unavailable: faces are marked by hand. */
  detection: 'pending' | 'done' | 'unavailable';
  faces: StorybookTaggedFace[];
}

/** One person the parent can tag, in cast order (hero first). */
export interface StorybookTaggablePerson {
  /** Stable while the form is edited (a companion keeps it when the one before is removed). */
  key: string;
  /** The id sent to the server for this person, from their place in the cast. */
  id: StorybookPersonId;
  name: string;
  /** False for a friend (and a companion whose relation is not chosen yet). */
  photoAllowed: boolean;
  /** Drawn from this FaceLock avatar (by name) instead of photos: never tagged. */
  avatar?: string;
}

export interface StorybookTagLimits {
  maxPhotos: number;
  maxRefsPerPerson: number;
  minFacePx: number;
  maxPhotoBytes: number;
  maxTotalPhotoBytes: number;
  photoLongEdge: number;
}

const positive = (value: number | undefined, fallback: number) =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback;

/** config.limits with the spec's defaults for anything an older backend does not send. */
export function storybookTagLimits(limits?: StorybookLimits | null): StorybookTagLimits {
  return {
    maxPhotos: positive(limits?.maxPhotos, 6),
    maxRefsPerPerson: positive(limits?.maxRefsPerPerson, 3),
    minFacePx: positive(limits?.minFacePx, 48),
    maxPhotoBytes: positive(limits?.maxPhotoBytes, STORYBOOK_DEFAULT_MAX_PHOTO_BYTES),
    maxTotalPhotoBytes: positive(limits?.maxTotalPhotoBytes, 6 * 1024 * 1024),
    photoLongEdge: positive(limits?.photoLongEdge, STORYBOOK_DEFAULT_PHOTO_LONG_EDGE),
  };
}

/** The shorter side of a face's box, in the photo's own pixels (the server's minFacePx check). */
export function faceSidePx(box: StorybookFaceBox, photo: Pick<StorybookPhotoItem, 'width' | 'height'>): number {
  return Math.min((box[2] - box[0]) * photo.width, (box[3] - box[1]) * photo.height);
}

export function faceTooSmall(
  box: StorybookFaceBox,
  photo: Pick<StorybookPhotoItem, 'width' | 'height'>,
  limits: Pick<StorybookTagLimits, 'minFacePx'>,
): boolean {
  // Only float noise is forgiven (48 / width * width is not always 48): the
  // payload rounds boxes outward (buildPhotoPayload), never by more than it
  // has to, so a box that passes here passes the server's check too.
  return faceSidePx(box, photo) < limits.minFacePx - 1e-9;
}

const isPerson = (person: string | null): person is string => Boolean(person) && person !== NOT_IN_BOOK;

/** In how many photos a person is tagged. */
export function refsOf(photos: StorybookPhotoItem[], personKey: string): number {
  return photos.reduce((n, photo) => n + (photo.faces.some((face) => face.person === personKey) ? 1 : 0), 0);
}

/** Photos per person (people who may be photographed), for the step's summary. */
export function photoCoverage(photos: StorybookPhotoItem[], people: StorybookTaggablePerson[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const person of people) out[person.key] = person.photoAllowed ? refsOf(photos, person.key) : 0;
  return out;
}

export type StorybookAssignBlock = 'too_small' | 'not_allowed' | 'in_photo' | 'max_refs';

/** Why `person` can't be given this face (null: it can). NOT_IN_BOOK and null are always allowed. */
export function assignBlock(
  photos: StorybookPhotoItem[],
  photoKey: string,
  faceKey: string,
  person: string | null,
  people: StorybookTaggablePerson[],
  limits: Pick<StorybookTagLimits, 'minFacePx' | 'maxRefsPerPerson'>,
): StorybookAssignBlock | null {
  if (!isPerson(person)) return null;
  const photo = photos.find((p) => p.key === photoKey);
  const face = photo?.faces.find((f) => f.key === faceKey);
  if (!photo || !face) return 'not_allowed';
  if (faceTooSmall(face.box, photo, limits)) return 'too_small';
  const who = people.find((p) => p.key === person);
  if (!who || !who.photoAllowed) return 'not_allowed';
  if (face.person === person) return null;
  if (photo.faces.some((f) => f.key !== faceKey && f.person === person)) return 'in_photo';
  if (refsOf(photos, person) >= limits.maxRefsPerPerson) return 'max_refs';
  return null;
}

function mapFaces(
  photos: StorybookPhotoItem[],
  photoKey: string,
  fn: (faces: StorybookTaggedFace[]) => StorybookTaggedFace[],
): StorybookPhotoItem[] {
  return photos.map((photo) => (photo.key === photoKey ? { ...photo, faces: fn(photo.faces) } : photo));
}

/** Sets who a face is (the caller checked assignBlock). */
export function assignFace(photos: StorybookPhotoItem[], photoKey: string, faceKey: string, person: string | null): StorybookPhotoItem[] {
  return mapFaces(photos, photoKey, (faces) => faces.map((face) => (face.key === faceKey ? { ...face, person } : face)));
}

export function removeFace(photos: StorybookPhotoItem[], photoKey: string, faceKey: string): StorybookPhotoItem[] {
  return mapFaces(photos, photoKey, (faces) => faces.filter((face) => face.key !== faceKey));
}

export function updateFaceBox(photos: StorybookPhotoItem[], photoKey: string, faceKey: string, box: StorybookFaceBox): StorybookPhotoItem[] {
  return mapFaces(photos, photoKey, (faces) => faces.map((face) => (face.key === faceKey ? { ...face, box } : face)));
}

/**
 * Forgets every face tagged as `personKey` (they left the cast, or became a
 * friend): the face goes back to "not chosen yet".
 */
export function untagPerson(photos: StorybookPhotoItem[], personKey: string): StorybookPhotoItem[] {
  if (!photos.some((photo) => photo.faces.some((face) => face.person === personKey))) return photos;
  return photos.map((photo) => ({
    ...photo,
    faces: photo.faces.map((face) => (face.person === personKey ? { ...face, person: null } : face)),
  }));
}

/** A face of someone in the cast (not "not in the book", not unchosen, not too small). */
function usableFace(photo: StorybookPhotoItem, face: StorybookTaggedFace, people: StorybookTaggablePerson[], limits: Pick<StorybookTagLimits, 'minFacePx'>): boolean {
  if (!isPerson(face.person)) return false;
  const who = people.find((p) => p.key === face.person);
  return Boolean(who?.photoAllowed) && !faceTooSmall(face.box, photo, limits);
}

/**
 * Whether a person is drawn from a photo: at least one of their tagged faces
 * is sent (a usable face). Everyone who is needs their skin tone and hair
 * colour chosen (docs/storybook-identity-lock.md §12): the worker sees faces
 * in grayscale, and the server refuses them without.
 */
export function hasSentFace(
  photos: StorybookPhotoItem[],
  personKey: string,
  people: StorybookTaggablePerson[],
  limits: Pick<StorybookTagLimits, 'minFacePx'>,
): boolean {
  return photos.some((photo) => photo.faces.some((face) => face.person === personKey && usableFace(photo, face, people, limits)));
}

/**
 * A person's largest usable tagged face that the detector found (the shorter
 * box side in photo pixels; the first one on a tie): the face their skin and
 * hair colour SUGGESTION is read from (§12; never applied by itself). A box
 * placed by hand is never read: it can sit off the face.
 */
export function bestTaggedFace(
  photos: StorybookPhotoItem[],
  personKey: string,
  people: StorybookTaggablePerson[],
  limits: Pick<StorybookTagLimits, 'minFacePx'>,
): { photo: StorybookPhotoItem; face: StorybookTaggedFace } | null {
  let best: { photo: StorybookPhotoItem; face: StorybookTaggedFace; side: number } | null = null;
  for (const photo of photos) {
    for (const face of photo.faces) {
      if (face.person !== personKey || face.source === 'manual' || !usableFace(photo, face, people, limits)) continue;
      const side = faceSidePx(face.box, photo);
      if (!best || side > best.side) best = { photo, face, side };
    }
  }
  return best && { photo: best.photo, face: best.face };
}

/** A photo is sent only when someone of the cast is tagged in it. */
export function photoIsUsed(
  photo: StorybookPhotoItem,
  people: StorybookTaggablePerson[],
  limits: Pick<StorybookTagLimits, 'minFacePx'>,
): boolean {
  return photo.faces.some((face) => usableFace(photo, face, people, limits));
}

/** Decoded bytes of every photo so far (the budget new photos are prepared against). */
export function photoBytes(photos: StorybookPhotoItem[]): number {
  return photos.reduce((n, photo) => n + photo.bytes, 0);
}

// Five decimals is 0.02 px on a 2048px photo. Every edge moves strictly
// OUTWARD (an edge already on the grid moves one step), so a face at exactly
// minFacePx can't come out a hair under the server's check on the way.
const floor5 = (v: number) => Math.max(0, Math.floor(v * 1e5 - 1e-6) / 1e5);
const ceil5 = (v: number) => Math.min(1, Math.ceil(v * 1e5 + 1e-6) / 1e5);
const outward = ([x0, y0, x1, y1]: StorybookFaceBox): StorybookFaceBox => [floor5(x0), floor5(y0), ceil5(x1), ceil5(y1)];

/** A face's box exactly as the request sends it (the one its colour suggestion is read from). */
export const payloadFaceBox = (box: StorybookFaceBox): StorybookFaceBox => outward(box);

/** The server's cap on photos[i].others (routes/storybook.js MAX_OTHER_FACES): more fails the whole request. */
export const STORYBOOK_MAX_OTHER_FACES = 20;

const validBox = (box: StorybookFaceBox) =>
  box.every((v) => Number.isFinite(v) && v >= 0 && v <= 1) && box[2] > box[0] && box[3] > box[1];

/**
 * How far `box` stays from the nearest tagged face, in that face's own sizes
 * (the gap in photo pixels over the face's longer side). A stored crop is a
 * square a few face sizes wide around its face, so the smaller this is, the
 * deeper the box reaches into a crop.
 */
function reachOf(box: StorybookFaceBox, tagged: StorybookFaceBox[], photo: Pick<StorybookPhotoItem, 'width' | 'height'>): number {
  let best = Infinity;
  for (const t of tagged) {
    const gapX = Math.max(0, box[0] - t[2], t[0] - box[2]) * photo.width;
    const gapY = Math.max(0, box[1] - t[3], t[1] - box[3]) * photo.height;
    const side = Math.max((t[2] - t[0]) * photo.width, (t[3] - t[1]) * photo.height, 1);
    best = Math.min(best, Math.max(gapX, gapY) / side);
  }
  return best;
}

/**
 * photos[i].others: every face of the photo that is not a usable tag
 * (untagged, "not in the book", a friend, too small), rounded outward so the
 * blur covers at least the marked box. Past the server's cap, the faces
 * nearest a tagged face are kept (a far one never reaches a crop); the order
 * of the photo is kept.
 */
function otherFaces(
  photo: StorybookPhotoItem,
  tagged: StorybookTaggedFace[],
  others: StorybookTaggedFace[],
): StorybookFaceBox[] {
  const boxes = others.map((face) => outward(face.box)).filter(validBox);
  if (boxes.length <= STORYBOOK_MAX_OTHER_FACES) return boxes;
  const taggedBoxes = tagged.map((face) => face.box);
  const keep = new Set(
    boxes
      .map((box, i) => ({ i, reach: reachOf(box, taggedBoxes, photo) }))
      .sort((a, b) => a.reach - b.reach || a.i - b.i)
      .slice(0, STORYBOOK_MAX_OTHER_FACES)
      .map(({ i }) => i),
  );
  return boxes.filter((_, i) => keep.has(i));
}

export interface StorybookPhotoPayload {
  photos: StorybookPhotoUpload[];
  faces: StorybookFaceTag[];
  /** Which photo / face each entry came from, so a server error on photos[i] or faces[i] can be shown on it. */
  sent: { photos: string[]; faces: Array<{ photo: string; face: string }> };
}

/**
 * The request's photos (ids p1, p2, … in the order they were added, only
 * photos with a face of the cast) and faces (normalized boxes in the SENT
 * photo's pixels: the prepared JPEG is sent as is). A photo also carries the
 * boxes of everyone else found or marked in it (`others`, left out when
 * none), so no bystander's face is kept in a stored crop.
 */
export function buildPhotoPayload(
  photos: StorybookPhotoItem[],
  people: StorybookTaggablePerson[],
  limits: Pick<StorybookTagLimits, 'minFacePx'>,
): StorybookPhotoPayload {
  const out: StorybookPhotoPayload = { photos: [], faces: [], sent: { photos: [], faces: [] } };
  for (const photo of photos) {
    const faces = photo.faces.filter((face) => usableFace(photo, face, people, limits));
    if (!faces.length) continue;
    const id = `p${out.photos.length + 1}`;
    const others = otherFaces(photo, faces, photo.faces.filter((face) => !faces.includes(face)));
    out.photos.push(others.length ? { id, data: photo.dataUrl, others } : { id, data: photo.dataUrl });
    out.sent.photos.push(photo.key);
    for (const face of faces) {
      const who = people.find((p) => p.key === face.person) as StorybookTaggablePerson;
      out.faces.push({ photo: id, person: who.id, box: outward(face.box) });
      out.sent.faces.push({ photo: photo.key, face: face.key });
    }
  }
  return out;
}

/**
 * A square box (square in PIXELS) of `sidePx` centred on (cx, cy), both
 * normalized; shifted to stay inside the photo, and never larger than its
 * short side. The shape of a hand-placed face.
 */
export function squareFaceBox(
  cx: number,
  cy: number,
  sidePx: number,
  photo: Pick<StorybookPhotoItem, 'width' | 'height'>,
): StorybookFaceBox {
  const side = Math.max(1, Math.min(sidePx, photo.width, photo.height));
  const w = side / photo.width;
  const h = side / photo.height;
  const x0 = Math.min(1 - w, Math.max(0, cx - w / 2));
  const y0 = Math.min(1 - h, Math.max(0, cy - h / 2));
  return [x0, y0, x0 + w, y0 + h];
}

/** The side, in photo pixels, of a hand-placed box (its longer side, as it was square when placed). */
export function boxSidePx(box: StorybookFaceBox, photo: Pick<StorybookPhotoItem, 'width' | 'height'>): number {
  return Math.round(Math.max((box[2] - box[0]) * photo.width, (box[3] - box[1]) * photo.height));
}

export function boxCentre(box: StorybookFaceBox): [number, number] {
  return [(box[0] + box[2]) / 2, (box[1] + box[3]) / 2];
}
